import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { authorsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, UserCircle, Camera } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const AdminAuthors = () => {
  const { token } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", photo: "" });

  useEffect(() => { fetchAuthors(); }, []);

  const fetchAuthors = async () => {
    try {
      const res = await authorsAPI.getAll();
      setAuthors(res.data);
    } catch { toast.error("Failed to load authors"); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ name: "", bio: "", photo: "" }); setEditingAuthor(null); };

  const handleEdit = (a) => {
    setEditingAuthor(a);
    setForm({ name: a.name, bio: a.bio || "", photo: a.photo || "" });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editingAuthor) {
        await authorsAPI.update(editingAuthor.id, form, token);
        toast.success("Author updated");
      } else {
        await authorsAPI.create(form, token);
        toast.success("Author created");
      }
      setShowModal(false);
      resetForm();
      fetchAuthors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save author");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this author? Any assigned blog posts will be unlinked.")) return;
    try {
      await authorsAPI.delete(id, token);
      toast.success("Author deleted");
      fetchAuthors();
    } catch { toast.error("Failed to delete author"); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-authors">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">Authors</h1>
            <p className="text-muted-foreground">Manage blog author profiles</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="rounded-full" data-testid="add-author-btn">
            <Plus className="w-4 h-4 mr-2" />New Author
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : authors.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {authors.map((author) => (
              <div key={author.id} className="bg-white rounded-2xl shadow-soft p-5 flex flex-col items-center text-center" data-testid={`author-card-${author.id}`}>
                {author.photo ? (
                  <img src={author.photo} alt={author.name} className="w-20 h-20 rounded-full object-cover mb-3 ring-2 ring-primary/20" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-3">
                    <UserCircle className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
                <h3 className="font-semibold text-foreground text-lg">{author.name}</h3>
                {author.bio && <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{author.bio}</p>}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(author)} data-testid={`edit-author-${author.id}`}>
                    <Pencil className="w-4 h-4 mr-1" />Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(author.id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-author-${author.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <UserCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Authors Yet</h3>
            <p className="text-muted-foreground mb-6">Create author profiles to assign to blog posts.</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />Create First Author
            </Button>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingAuthor ? "Edit Author" : "New Author"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="author-form">
              <div>
                <label className="text-sm font-medium mb-1 block">Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Author name"
                  data-testid="form-author-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Bio</label>
                <Textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  placeholder="Short bio about the author"
                  rows={3}
                  data-testid="form-author-bio"
                />
              </div>
              <ImageUpload
                value={form.photo}
                onChange={(url) => setForm({ ...form, photo: url })}
                token={token}
                label="Profile Photo"
              />
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="form-author-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingAuthor ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAuthors;
